import { ArrowLeft2, ShieldTick } from 'iconsax-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useRouteLoaderData } from 'react-router';
import { useTypedParams, useTypedSearchParams, useTypedState } from 'react-router-typesafe-routes';
import Badge from '@/components/common/badge';
import HeaderActionButton from '@/components/common/header-action-button';
import ConversationVirtuaList from '@/components/conversations/conversation-list/conversation-virtua-list';
import { ConversationNoticeBar } from '@/components/conversations/conversation-notice-bar';
import ConversationProviders from '@/components/conversations/conversation-providers';
import MessageStateIndicator from '@/components/conversations/message-state-indicator';
import SessionSettingMenu from '@/components/conversations/session-setting-menu';
import { WebSocketStateIndicator } from '@/components/conversations/web-socket-state-indicator';
import AppHeader from '@/components/layouts/page-layout/app-header';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import useIsAdminSession from '@/hooks/conversations/use-is-admin-session';
import useGroupInfo from '@/hooks/groups/use-group-info';
import { useGroupMessages } from '@/hooks/groups/use-group-messages';
import useResetScrollYOnFocus from '@/hooks/use-reset-scroll-y-on-focus';
import useRotateResize from '@/hooks/use-rotate-resize';
import useUserPlatform, { useShouldRetractRightSection } from '@/hooks/use-user-platform';
import ROUTES from '@/routes';
import type { GroupMessagesLayoutLoaderReturnType } from '@/routes/messages/groups/_layout.loader';
import { createConversationStore } from '@/stores/conversation';
import { useEmbedLayout } from '@/stores/device';
import { useMessageStore } from '@/stores/message';
import { EmbedLayout } from '@/utils/embed-layout';

interface Props {
  groupId: number;
}

export function GroupMessages({ groupId }: Props) {
  const layoutLoaderData = useRouteLoaderData<GroupMessagesLayoutLoaderReturnType>(ROUTES.MESSAGES.GROUPS.$path());
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDesktop, isEmbedded } = useUserPlatform();
  const [{ fromUnread }] = useTypedSearchParams(ROUTES.MESSAGES.GROUPS);
  const { from: navigatedFrom } = useTypedState(ROUTES.MESSAGES.GROUPS);
  const isAdminSession = useIsAdminSession('group', groupId);
  const layout = useEmbedLayout();
  const store = useMemo(() => createConversationStore('group', groupId, isAdminSession), [groupId, isAdminSession]);
  const { searchId, listKey } = useMessageStore();
  const { session, group, hasUnreadNotice } = useGroupMessages(groupId);
  const { data: groupInfo, isChannel, canManage } = useGroupInfo(groupId);
  const { dispatch } = useCommDispatcher();
  const shouldRetractRightSection = useShouldRetractRightSection();

  const promise = useMemo(() => layoutLoaderData?.promises || false, [groupId]);

  useResetScrollYOnFocus();
  useRotateResize();

  const handleNavigateBack = () => {
    if (isEmbedded) {
      dispatch({
        event: 'exit-session',
        data: { type: 'group', id: groupId },
      });

      if (navigatedFrom === 'comm') dispatch({ event: 'dismiss' });
    }
  };

  return (
    <PageLayout
      title={session?.GroupName || group?.GroupName || t('conversation.group.title')}
      className='gap-0 pb-safe-or-0 has-[.ql-editor:focus]:mode-standalone:pb-0'>
      {layout >= EmbedLayout.COMPACT && (
        <AppHeader
          navigateBack={isDesktop ? false : Boolean(searchId || navigatedFrom === 'search') || true}
          onNavigateBack={handleNavigateBack}
          className='desktop:px-6 desktop:border-b border-base-200 mb-0'
          actions={
            <>
              <MessageStateIndicator />
              <WebSocketStateIndicator />
              <SessionSettingMenu
                type='group'
                id={groupId}
              />
            </>
          }>
          {isDesktop && shouldRetractRightSection && (
            <button
              type='button'
              className='btn btn-ghost btn-square mr-2'
              onClick={() => {
                navigate(-1);
                useMessageStore.getState().resetKey();
              }}>
              <ArrowLeft2
                variant='Linear'
                className='size-6'
              />
            </button>
          )}
          <Link
            className='w-full desktop:w-fit'
            to={
              isDesktop
                ? ROUTES.MESSAGES.GROUPS.SETTINGS.MEMBERS.$buildPath({
                    params: { id: groupId },
                  })
                : ROUTES.MESSAGES.GROUPS.$.SETTINGS.$path({ relative: true })
            }
            state={
              navigatedFrom
                ? ROUTES.MESSAGES.GROUPS.SETTINGS.$buildState({
                    state: { from: 'comm' },
                  })
                : undefined
            }>
            <div className='flex w-full items-center justify-between gap-2'>
              <h3 className='flex items-center gap-1 text-base font-bold'>
                <span className='line-clamp-1 max-w-fit flex-1 break-all'>
                  {session?.GroupName ?? group?.GroupName}
                </span>

                {session?.GroupType === 1 ? (
                  <ShieldTick
                    variant='Bold'
                    className='size-6 text-primary'
                  />
                ) : null}

                <Badge show={canManage && !isChannel}>
                  {groupInfo?.GroupJoinApplyCount
                    ? groupInfo.GroupJoinApplyCount > 99
                      ? '99+'
                      : groupInfo.GroupJoinApplyCount
                    : null}
                </Badge>
              </h3>
              <HeaderActionButton />
            </div>
          </Link>
        </AppHeader>
      )}

      {group && hasUnreadNotice && <ConversationNoticeBar group={group} />}

      <ConversationProviders
        type='group'
        id={groupId}
        store={store}>
        <LoadingLayout
          className='bg-base-100'
          promise={promise}>
          {() =>
            session && (
              <ConversationVirtuaList
                key={listKey}
                searchId={searchId}
                initialUnreadCount={layoutLoaderData?.initialUnreadCount}
                lastMsgID={session.LastMsgID}
                lastReadId={layoutLoaderData?.lastReadId}
                isFromUnread={Boolean(fromUnread)}
              />
            )
          }
        </LoadingLayout>
      </ConversationProviders>
    </PageLayout>
  );
}

export default function GroupMessagesByIdRoute() {
  const { id } = useTypedParams(ROUTES.MESSAGES.GROUPS);

  return <GroupMessages groupId={id} />;
}
